import type { ContentBlock } from "@/types/page";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { HeroBlock } from "@/components/blocks/HeroBlock";
import { TextBlock } from "@/components/blocks/TextBlock";
import { CtaBlock } from "@/components/blocks/CtaBlock";
import { ImageBlock } from "@/components/blocks/ImageBlock";
import { VideoBlock } from "@/components/blocks/VideoBlock";
import { EmbedBlock } from "@/components/blocks/EmbedBlock";
import { SpacerBlock } from "@/components/blocks/SpacerBlock";
import { DividerBlock } from "@/components/blocks/DividerBlock";
import { QuoteBlock } from "@/components/blocks/QuoteBlock";
import { ButtonBlock } from "@/components/blocks/ButtonBlock";
import { ColumnsBlock } from "@/components/blocks/ColumnsBlock";
import { GroupBlock } from "@/components/blocks/GroupBlock";
import { PricingBlock } from "@/components/blocks/PricingBlock";
import { TestimonialsBlock } from "@/components/blocks/TestimonialsBlock";
import { MapBlock } from "@/components/blocks/MapBlock";
import { FormBlock } from "@/components/blocks/FormBlock";
import { FeatureGridBlock } from "@/components/blocks/FeatureGridBlock";
import { LogoStripBlock } from "@/components/blocks/LogoStripBlock";
import { StatsBandBlock } from "@/components/blocks/StatsBandBlock";
import { ImageTextSplitBlock } from "@/components/blocks/ImageTextSplitBlock";
import { GalleryCarouselBlock } from "@/components/blocks/GalleryCarouselBlock";
import { TeamPreviewBlock } from "@/components/blocks/TeamPreviewBlock";
import { CtaBannerBlock } from "@/components/blocks/CtaBannerBlock";
import { SocialLinksBlock } from "@/components/blocks/SocialLinksBlock";
import { MusicReleasesBlock } from "@/components/blocks/MusicReleasesBlock";
import { RadioHomePreviewBlock } from "@/components/blocks/RadioHomePreviewBlock";
import { SunnyIntroBlock } from "@/components/blocks/SunnyIntroBlock";
import { EventsPreviewBlock } from "@/components/blocks/EventsPreviewBlock";
import { NewsFeedBlock } from "@/components/blocks/NewsFeedBlock";

// Typed loosely so both sync and async server components can live in the same
// registry without fighting React 19 / @types/react JSX constructor types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BLOCKS: Record<string, any> = {
  hero: HeroBlock,
  text: TextBlock,
  cta: CtaBlock,
  image: ImageBlock,
  video: VideoBlock,
  embed: EmbedBlock,
  spacer: SpacerBlock,
  divider: DividerBlock,
  quote: QuoteBlock,
  button: ButtonBlock,
  columns: ColumnsBlock,
  group: GroupBlock,
  icon_text: TextBlock,
  testimonials: TestimonialsBlock,
  pricing: PricingBlock,
  accordion: TextBlock,
  map: MapBlock,
  form: FormBlock,
  table: TextBlock,
  feature_grid: FeatureGridBlock,
  logo_strip: LogoStripBlock,
  stats_band: StatsBandBlock,
  image_text_split: ImageTextSplitBlock,
  gallery_carousel: GalleryCarouselBlock,
  team_preview: TeamPreviewBlock,
  cta_banner: CtaBannerBlock,
  social_links: SocialLinksBlock,
  music_releases: MusicReleasesBlock,
  radio_home_preview: RadioHomePreviewBlock,
  sunny_intro: SunnyIntroBlock,
  events_preview: EventsPreviewBlock,
  news_feed: NewsFeedBlock,
};

export function BlockRenderer({
  blocks,
  template = "classic",
  heroStartsAtH2 = false,
}: {
  blocks: ContentBlock[];
  template?: SiteTemplateKey;
  heroStartsAtH2?: boolean;
}) {
  let usedHeroH1 = heroStartsAtH2;
  return (
    <>
      {blocks
        .filter((b) => b.published)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((block) => {
          const Component = BLOCKS[block.blockType] ?? TextBlock;
          const headingLevel = block.blockType === "hero" && !usedHeroH1 ? 1 : 2;
          if (block.blockType === "hero") {
            usedHeroH1 = true;
          }
          return (
            <Component
              key={block.id}
              content={block.content}
              template={template}
              headingLevel={headingLevel}
            />
          );
        })}
    </>
  );
}
